/**
 * API Client for customer-frontend
 * 
 * Handles HTTP requests to the backend API with authentication support.
 * Includes automatic handling of verification errors (DATA_VERIFICATION_REQUIRED, ID_VERIFICATION_REQUIRED).
 */

import { getApiUrl } from './env';
import { handleVerificationError } from './verification-handler';
import { storage } from './storage';

/**
 * Endpoints that are safe to cache via Service Worker.
 * These don't contain user-specific data and change infrequently.
 * Must match the patterns in public/sw.js API_CACHE_CONFIG.
 */
const CACHEABLE_ENDPOINTS = [
  // Tier 1: Static/config data (1 hour cache)
  '/lobby/init', // Unified lobby data (categories, providers, jackpot)
  '/loyalty/vip-data',
  '/game/categories',
  '/game/providers',
  '/help-center/article-categories',
  '/help-center/faqs',
  '/help-center/contact-channels',
  '/help-center/articles/',
  '/vip/faqs',
  '/jackpot/faqs',
  '/jackpot/group-config/',
  '/referral/bonus-tiers',
  '/referral/max-sc-reward',
  '/amoe/content',
  '/auth/social/providers',
  '/site-config/public/social',
  '/site-config/public/purchase-limits',
  // NOTE: /cms-pages intentionally NOT cached - T&C updates require users to see latest version immediately
  // Tier 2: Slowly changing content (60 min cache)
  '/game/all',
  '/game/by-category/',
  '/iconic21/get-available-games',
  '/promotions/customer',
];

/**
 * Check if an endpoint is cacheable by Service Worker
 */
function isCacheableEndpoint(endpoint: string): boolean {
  return CACHEABLE_ENDPOINTS.some(pattern => {
    if (pattern.endsWith('/')) {
      // Prefix match
      return endpoint.startsWith(pattern) || endpoint.includes(pattern);
    }
    // Exact match or starts with
    return endpoint === pattern || endpoint.startsWith(pattern);
  });
}

class ApiClient {
  private authToken: string | null = null;
  private baseUrl: string;

  constructor() {
    this.baseUrl = getApiUrl();
  }

  /**
   * Set the authentication token for API requests
   */
  setAuthToken(token: string | null): void {
    this.authToken = token;
    if (token) {
      storage.setItem('auth_token', token);
    } else {
      storage.removeItem('auth_token');
    }
  }

  /**
   * Get the current authentication token
   */
  getAuthToken(): string | null {
    if (!this.authToken) {
      this.authToken = storage.getItem('auth_token');
    }
    return this.authToken;
  }

  /**
   * Make an API request
   * 
   * For cacheable endpoints (defined in CACHEABLE_ENDPOINTS), requests are made
   * without cache-busting to allow Service Worker caching with stale-while-revalidate.
   * For all other endpoints, cache-busting ensures fresh data.
   */
  async request(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getAuthToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Check if this endpoint is cacheable by Service Worker
    const cacheable = isCacheableEndpoint(endpoint);
    const isGetRequest = (options.method || 'GET').toUpperCase() === 'GET';
    const isPostRequest = (options.method || 'GET').toUpperCase() === 'POST';
    
    // For cacheable POST endpoints (/game/all, /iconic21/get-available-games)
    const isCacheablePost = isPostRequest && 
      (endpoint.includes('/game/all') || endpoint.includes('/iconic21/get-available-games'));
    
    let finalUrl = url;
    let cacheMode: RequestCache = 'default';
    
    if (cacheable || isCacheablePost) {
      // Let Service Worker handle caching - don't bust cache
      // Service Worker uses stale-while-revalidate pattern
      cacheMode = 'default';
    } else if (isGetRequest) {
      // Non-cacheable GET requests: add cache-busting timestamp
      finalUrl = `${url}${url.includes('?') ? '&' : '?'}_t=${Date.now()}`;
      cacheMode = 'no-store';
    } else {
      // Mutations (POST/PUT/DELETE except cacheable POSTs): no cache
      cacheMode = 'no-store';
    }

    return fetch(finalUrl, {
      ...options,
      headers,
      credentials: 'include', // Include cookies in requests
      cache: cacheMode,
    });
  }

  /**
   * GET request
   */
  async get<T = any>(endpoint: string): Promise<T> {
    const response = await this.request(endpoint, { method: 'GET' });
    
    // Handle 304 Not Modified - treat as error and force refresh
    if (response.status === 304) {
      // Retry with a new timestamp to bypass cache
      const retryResponse = await this.request(`${endpoint}${endpoint.includes('?') ? '&' : '?'}_t=${Date.now()}&_retry=1`, { method: 'GET' });
      if (!retryResponse.ok) {
        let errorData: any = { message: retryResponse.statusText || 'Internal server error' };
        try {
          const text = await retryResponse.text();
          if (text && text.trim()) {
            try {
              errorData = JSON.parse(text);
            } catch {
              errorData = { message: text || retryResponse.statusText || 'Internal server error' };
            }
          }
        } catch {
          errorData = { message: retryResponse.statusText || 'Internal server error' };
        }
        const errorObj = new Error(errorData.message || `HTTP ${retryResponse.status}`);
        (errorObj as any).status = retryResponse.status;
        (errorObj as any).response = { status: retryResponse.status, data: errorData, message: errorData.message };
        throw errorObj;
      }
      return retryResponse.json();
    }
    
    if (!response.ok) {
      // Try to parse error response, but handle all possible failure cases gracefully
      let errorData: any = { message: response.statusText || 'Internal server error' };
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const text = await response.text();
          if (text && text.trim()) {
            try {
              errorData = JSON.parse(text);
            } catch (parseError) {
              // If JSON parsing fails, use the text as message
              errorData = { message: text || response.statusText || 'Internal server error' };
            }
          }
        } else {
          // Non-JSON response
          try {
            const text = await response.text();
            errorData = { message: text || response.statusText || 'Internal server error' };
          } catch (textError) {
            // If reading text fails, use status text
            errorData = { message: response.statusText || 'Internal server error' };
          }
        }
      } catch (error) {
        // If all error parsing fails, use default
        errorData = { message: response.statusText || 'Internal server error' };
      }
      
      // Create error object with proper structure for error handling
      const errorObj = new Error(errorData.message || `HTTP ${response.status}`);
      (errorObj as any).status = response.status;
      (errorObj as any).response = {
        status: response.status,
        data: errorData,
        message: errorData.message,
        error: errorData.error || errorData.code,
      };
      
      // Handle verification errors globally (shows modal/toast automatically)
      handleVerificationError(errorObj);
      
      throw errorObj;
    }
    
    // Check if response has content before parsing JSON
    const contentType = response.headers.get('content-type');
    const text = await response.text();
    
    if (!text || text.trim() === '') {
      // Empty response, return empty object
      return {} as T;
    }
    
    if (contentType && contentType.includes('application/json')) {
      try {
        return JSON.parse(text);
      } catch (e) {
        console.error('[API Client] Failed to parse JSON response:', e, text);
        throw new Error('Invalid JSON response from server');
      }
    }
    
    // If not JSON, try to parse anyway (fallback)
    try {
      return JSON.parse(text);
    } catch (e) {
      return text as any;
    }
  }

  /**
   * POST request
   */
  async post<T = any>(endpoint: string, body?: any): Promise<T> {
    const response = await this.request(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      // Try to parse JSON error response
      const contentType = response.headers.get('content-type');
      let error: any;
      
      if (contentType && contentType.includes('application/json')) {
        try {
          const text = await response.text();
          error = text ? JSON.parse(text) : { message: response.statusText };
        } catch (e) {
          error = { message: response.statusText };
        }
      } else {
        // Non-JSON error response
        try {
          const text = await response.text();
          error = { message: text || response.statusText };
        } catch (e) {
          error = { message: response.statusText };
        }
      }
      
      // Create error object with status code and error code
      const errorObj = new Error(error.message || `HTTP ${response.status}`);
      (errorObj as any).status = response.status;
      (errorObj as any).response = error;
      (errorObj as any).code = error.error || error.code; // Preserve error code from backend
      
      // Handle verification errors globally (shows modal/toast automatically)
      handleVerificationError(errorObj);
      
      throw errorObj;
    }
    
    // Check if response has content before parsing JSON
    const contentType = response.headers.get('content-type');
    const text = await response.text();
    
    if (!text || text.trim() === '') {
      // Empty response, return empty object
      return {} as T;
    }
    
    if (contentType && contentType.includes('application/json')) {
      try {
        return JSON.parse(text);
      } catch (e) {
        console.error('[API Client] Failed to parse JSON response:', e, text);
        throw new Error('Invalid JSON response from server');
      }
    }
    
    // If not JSON, try to parse anyway (fallback)
    try {
      return JSON.parse(text);
    } catch (e) {
      return text as any;
    }
  }

  /**
   * PUT request
   */
  async put<T = any>(endpoint: string, body?: any): Promise<T> {
    const response = await this.request(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      // Try to parse JSON error response
      const contentType = response.headers.get('content-type');
      let error: any;
      
      if (contentType && contentType.includes('application/json')) {
        try {
          const text = await response.text();
          error = text ? JSON.parse(text) : { message: response.statusText };
        } catch (e) {
          error = { message: response.statusText };
        }
      } else {
        // Non-JSON error response
        try {
          const text = await response.text();
          error = { message: text || response.statusText };
        } catch (e) {
          error = { message: response.statusText };
        }
      }
      
      // Create error object with status code and error code
      const errorObj = new Error(error.message || `HTTP ${response.status}`);
      (errorObj as any).status = response.status;
      (errorObj as any).response = error;
      (errorObj as any).code = error.error || error.code;
      
      // Handle verification errors globally (shows modal/toast automatically)
      handleVerificationError(errorObj);
      
      throw errorObj;
    }
    
    // Check if response has content before parsing JSON
    const contentType = response.headers.get('content-type');
    const text = await response.text();
    
    if (!text || text.trim() === '') {
      // Empty response, return empty object
      return {} as T;
    }
    
    if (contentType && contentType.includes('application/json')) {
      try {
        return JSON.parse(text);
      } catch (e) {
        console.error('[API Client] Failed to parse JSON response:', e, text);
        throw new Error('Invalid JSON response from server');
      }
    }
    
    // If not JSON, try to parse anyway (fallback)
    try {
      return JSON.parse(text);
    } catch (e) {
      return text as any;
    }
  }

  /**
   * DELETE request
   */
  async delete<T = any>(endpoint: string, body?: any): Promise<T> {
    const response = await this.request(endpoint, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      // Try to parse JSON error response
      const contentType = response.headers.get('content-type');
      let error: any;
      
      if (contentType && contentType.includes('application/json')) {
        try {
          const text = await response.text();
          error = text ? JSON.parse(text) : { message: response.statusText };
        } catch (e) {
          error = { message: response.statusText };
        }
      } else {
        // Non-JSON error response
        try {
          const text = await response.text();
          error = { message: text || response.statusText };
        } catch (e) {
          error = { message: response.statusText };
        }
      }
      
      // Create error object with status code and error code
      const errorObj = new Error(error.message || `HTTP ${response.status}`);
      (errorObj as any).status = response.status;
      (errorObj as any).response = error;
      (errorObj as any).code = error.error || error.code;
      
      // Handle verification errors globally (shows modal/toast automatically)
      handleVerificationError(errorObj);
      
      throw errorObj;
    }
    
    // Check if response has content before parsing JSON
    const contentType = response.headers.get('content-type');
    const text = await response.text();
    
    if (!text || text.trim() === '') {
      // Empty response, return empty object
      return {} as T;
    }
    
    if (contentType && contentType.includes('application/json')) {
      try {
        return JSON.parse(text);
      } catch (e) {
        console.error('[API Client] Failed to parse JSON response:', e, text);
        throw new Error('Invalid JSON response from server');
      }
    }
    
    // If not JSON, try to parse anyway (fallback)
    try {
      return JSON.parse(text);
    } catch (e) {
      return text as any;
    }
  }
}

// Export singleton instance
export const apiClient = new ApiClient();



